function _normalizePrice(val) {
  if (val == null) return "";
  return String(val).replace(/\s+/g, "").replace(",", ".");
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", (event) => {
      if (event.target.matches('#sortModal input[name="sort_by"]')) return;
      this.debouncedOnSubmit(event);
    });
    facetForm.addEventListener("submit", this.onSubmitHandler.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const subcollectionContext = FacetFiltersForm.getSubcollectionContext(searchParams);
    const sections = FacetFiltersForm.getSections();
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    FacetFiltersForm.toggleProductGridLoading(true);

    // Only the latest filter, sort or chip render may land: an older response arriving last would show the
    // previous selection's grid under the new one's active state.
    FacetFiltersForm.abortController?.abort();
    FacetFiltersForm.abortController = new AbortController();
    const { signal } = FacetFiltersForm.abortController;

    sections.forEach((section) => {
      const url = `${subcollectionContext.collectionUrl}?section_id=${section.section}&${subcollectionContext.searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, signal);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  // Parsed once into an inert <template> whose nodes are then moved into the page. DOMParser plus the grid's
  // innerHTML parsed the large section twice; template parsing keeps scripts inert and noscript as text, as innerHTML did.
  static parseSectionHTML(html) {
    const template = document.createElement("template");
    template.innerHTML = html;
    return template.content;
  }

  static renderSectionFromFetch(url, event, signal) {
    fetch(url, { signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        const parsedHTML = FacetFiltersForm.parseSectionHTML(html);
        FacetFiltersForm.renderFilters(parsedHTML, event);
        FacetFiltersForm.renderProductGridContainer(parsedHTML);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === "function") initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch((error) => {
        // Superseded by a newer render, which owns the loading state now.
        if (error?.name === "AbortError") return;
        FacetFiltersForm.toggleProductGridLoading(false);
        console.error("Unable to update product results", error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    const parsedHTML = FacetFiltersForm.parseSectionHTML(html);
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === "function") initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(parsedHTML) {
    const productGridContainer = document.getElementById("ProductGridContainer");
    productGridContainer.replaceChildren(...parsedHTML.getElementById("ProductGridContainer").childNodes);
    productGridContainer.setAttribute("aria-busy", "false");
    document.documentElement.classList.remove("subcollection-initial-loading");

    productGridContainer.querySelectorAll(".scroll-trigger").forEach((element) => {
      element.classList.add("scroll-trigger--cancel");
    });

    // Announce the new result count after the swap. The region is emptied first and filled after a
    // short delay so the live region reports it as a change; only pages that print a count do this.
    const countStatus = productGridContainer.querySelector("#CollectionLoadingStatus");
    const countText = countStatus?.dataset.countText;
    if (countStatus && countText) {
      countStatus.textContent = "";
      clearTimeout(FacetFiltersForm.countStatusTimer);
      FacetFiltersForm.countStatusTimer = setTimeout(() => {
        if (countStatus.isConnected) countStatus.textContent = countText;
      }, 100);
    }
  }

  static toggleProductGridLoading(isLoading) {
    const productGridContainer = document.getElementById("ProductGridContainer");
    if (!productGridContainer) return;

    productGridContainer.setAttribute("aria-busy", isLoading ? "true" : "false");
    productGridContainer.querySelector(".collection")?.classList.toggle("loading", isLoading);
    productGridContainer.querySelector(".collection-loading-spinner")?.classList.toggle("hidden", !isLoading);
    const loadingStatus = productGridContainer.querySelector("#CollectionLoadingStatus");
    if (loadingStatus) loadingStatus.textContent = isLoading ? loadingStatus.dataset.loadingText || "" : "";
    if (!isLoading) document.documentElement.classList.remove("subcollection-initial-loading");
  }

  static renderProductCount(html) {
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
    applyProductSwatches();
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest(".js-filter"));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets-mobile", ".active-facets-desktop"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [".mobile-facets__open", ".mobile-facets__count", ".sorting"];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById("FacetFiltersFormMobile")?.closest("menu-drawer").bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector("show-more-button .label-show-more.hidden"));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) => hiddenItem.classList.replace("hidden", "show-more-item"));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams && "?".concat(searchParams)}`);
  }

  static getSubcollectionContext(searchParams) {
    const params = new URLSearchParams(searchParams);
    const navigation = document.querySelector("[data-subcollection-filter]");
    const handle = params.get("subcollection");
    const selectedLink = handle
      ? Array.from(navigation?.querySelectorAll("[data-subcollection-handle]") || []).find(
          (link) => link.dataset.subcollectionHandle === handle,
        )
      : null;

    navigation?.querySelectorAll(".category-item").forEach((link) => {
      const isActive = selectedLink ? link === selectedLink : !link.dataset.subcollectionHandle;
      link.classList.toggle("active", isActive);
      link.querySelector(".body")?.classList.toggle("active", isActive);
      if (isActive) link.setAttribute("aria-current", "true");
      else link.removeAttribute("aria-current");
    });

    if (navigation) params.delete("subcollection");

    return {
      collectionUrl: selectedLink?.dataset.collectionUrl || window.location.pathname,
      searchParams: params.toString(),
    };
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const priceParamNames = ["filter.v.price.gte", "filter.v.price.lte"];

    priceParamNames.forEach((paramName) => {
      let rawValue = formData.get(paramName);
      if (!rawValue) {
        const inputEl = form.querySelector(`input[name="${CSS.escape(paramName)}"]`);
        rawValue = inputEl?.value ?? inputEl?.getAttribute("value") ?? "";
      }

      const normalizedValue = _normalizePrice(rawValue);
      if (normalizedValue && !Number.isNaN(Number(normalizedValue))) {
        formData.set(paramName, normalizedValue);
      } else {
        formData.delete(paramName);
      }
    });
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    const params = new URLSearchParams(searchParams);
    const activeSubcollection = document.querySelector(
      "[data-subcollection-filter] .category-item.active[data-subcollection-handle]",
    );

    if (activeSubcollection) params.set("subcollection", activeSubcollection.dataset.subcollectionHandle);

    FacetFiltersForm.renderPage(params.toString(), event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll("facet-filters-form form");
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(event.target.closest("form"));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest("form").id === "FacetFiltersFormMobile";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === "FacetSortForm" || form.id === "FacetFiltersForm" || form.id === "FacetSortDrawerForm") {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => {
      element.addEventListener("change", this.onRangeChange.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
    this.adjustToValidValues(event.target);
  }
  adjustToValidValues(input) {
    // Ensure debounce timer storage
    if (!this._adjustTimeouts) this._adjustTimeouts = new Map();

    // Clear any previous timeout for this input
    if (this._adjustTimeouts.has(input)) {
      clearTimeout(this._adjustTimeouts.get(input));
    }

    // Set new debounce
    const timeout = setTimeout(() => {
      const inputs = this.querySelectorAll("input");
      const minInput = inputs[0];
      const maxInput = inputs[1];

      if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
      if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
      if (minInput.value === "") maxInput.setAttribute("data-min", 0);
      if (maxInput.value === "") minInput.setAttribute("data-max", maxInput.getAttribute("data-max"));

      const value = parseFloat(input.value);
      const min = parseFloat(input.getAttribute("data-min"));
      const max = parseFloat(input.getAttribute("data-max"));

      if (value < min) input.value = min;
      if (value > max) input.value = max;
    }, 700); // 300ms debounce

    // Store the timeout for this input
    this._adjustTimeouts.set(input, timeout);
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
    if (window.innerWidth > 750) {
      // Close the filter modal (details tag) immediately after
      const models = document.querySelectorAll("details");
      models.forEach((model) => {
        if (model && model.open) {
          model.open = false;
        }
      });
    } else {
      const mobile_menu = form.closest("details");
      if (mobile_menu) {
        mobile_menu.removeAttribute("open");
        mobile_menu.classList.remove("menu-opening");
      }

      form.querySelectorAll("details").forEach((details) => {
        details.removeAttribute("open");
        details.classList.remove("menu-opening");
      });
      form.querySelectorAll(".submenu-open").forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
      document.body.classList.remove("overflow-hidden-mobile");
    }
  }
}

customElements.define("facet-remove", FacetRemove);
